import classNames from 'classnames';
import React, {useContext, useCallback} from 'react';

import {AsyncCourseDynamicPriceText, BrowseCourse} from '@udemy/browse-course';
import {InDemandCareer} from '@udemy/career-academies';
import {useI18n, useFormatNumber, LocalizedHtml} from '@udemy/i18n';
import {Icon} from '@udemy/icons';
import {Loader} from '@udemy/react-reveal-components';
import {PURCHASE_PRICE_TYPES} from '@udemy/shopping';
import {PlanPeriod} from '@udemy/subscription-browse';
import {useConsumerSubscriptionPlan} from '@udemy/subscription-graphql';
import {useFormatCurrency} from '@udemy/ud-data';

import {CareerAcceleratorCTAType, CTAContext} from '../cta-context/cta-context';
import {PostCtaExperienceButton} from '../post-cta-experience-button/post-cta-experience-button.react-component';
import styles from './purchase-cta.module.less';

const SubscriberCount = ({count}: {count: number}) => {
    const {formatNumber} = useFormatNumber();
    return <span className="ud-text-bold">{formatNumber(count, {notation: 'compact'})}</span>;
};

export type PurchaseCTAProps = {
    courses?: BrowseCourse[];
    className?: string;
    containerClassName?: string;
    subscriberCountClassName?: string;
    uiRegion?: string;
    academy: InDemandCareer;
};

export function PurchaseCTA({
    uiRegion,
    courses,
    className,
    containerClassName,
    subscriberCountClassName,
    academy,
}: PurchaseCTAProps) {
    const {formatCurrency} = useFormatCurrency();
    const personalPlanContext = useConsumerSubscriptionPlan();
    const {gettext} = useI18n();
    const {ctaType} = useContext(CTAContext);

    const getPlanPeriodProps = useCallback(
        (plan) => {
            return {
                listPriceText: formatCurrency(plan?.defaultPlanOption.price.monthly, {scale: 0}),
                discountPriceText: plan?.selectedDiscount?.subscriptionDiscountMonthlyPrice
                    ? formatCurrency(plan?.selectedDiscount?.subscriptionDiscountMonthlyPrice, {
                          scale: 0,
                      })
                    : undefined,
                showTrialDays: false,
                showStartingAtPrice: true,
                abbreviateStartingAtPrice: true,
                trialPeriodDays: plan?.trialPeriodDays,
            };
        },
        [formatCurrency],
    );

    return (
        <div className={containerClassName}>
            {!!courses?.length && (
                <div
                    className={classNames(styles['cta-container'], className)}
                    data-testid="cta-container"
                >
                    <PostCtaExperienceButton
                        academy={academy}
                        uiRegion={uiRegion}
                        courses={courses}
                        allowAddToCartSuccessModal={false}
                    />
                    {ctaType === CareerAcceleratorCTAType.LOADING && <Loader />}
                    {ctaType === CareerAcceleratorCTAType.PURCHASE_CTA_MX && (
                        <div className={styles['price-container']}>
                            <AsyncCourseDynamicPriceText
                                buyables={courses}
                                listPriceClassName={styles['price-original']}
                                discountPriceClassName={styles['price']}
                                trackingEventContext={{
                                    priceType: PURCHASE_PRICE_TYPES.bundle,
                                }}
                            />
                        </div>
                    )}
                    {ctaType === CareerAcceleratorCTAType.PURCHASE_CTA_PP && (
                        <div className={styles['personal-plan-disclaimer']}>
                            <div>
                                <PlanPeriod {...getPlanPeriodProps(personalPlanContext)} />
                            </div>
                        </div>
                    )}
                </div>
            )}
            {!!academy.totalSubscribers && (
                <div
                    className={classNames(
                        'ud-text-xs',
                        subscriberCountClassName,
                        styles['enrolled-count'],
                    )}
                >
                    <Icon
                        glyph="people"
                        size="xsmall"
                        label={gettext('Enrolled students')}
                        color="inherit"
                    />
                    <span>
                        <LocalizedHtml
                            html={gettext(
                                '<span class="totalSubscribers">%(totalSubscribers)s</span> learners already enrolled',
                            )}
                            interpolate={{
                                totalSubscribers: (
                                    <SubscriberCount count={academy.totalSubscribers} />
                                ),
                            }}
                        />
                    </span>
                </div>
            )}
        </div>
    );
}
